// StatusMonitoringSection.jsx
import React, { useMemo } from "react";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";

/** --- Visuals (same look) --- */
const Karta = ({ children, style }) => (
  <div
    style={{
      border: "1px solid #e6e9ee",
      borderRadius: 16,
      padding: 16,
      background: "#fff",
      boxShadow: "0 16px 48px rgba(16,24,40,0.16), 0 8px 22px rgba(16,24,40,0.12)",
      ...style,
    }}
  >
    {children}
  </div>
);

const Sekcja = ({ title, icon: Icon = Info, children }) => (
  <Karta>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      {Icon ? <Icon size={18} color="#0b74e5" /> : null}
      <h3 style={{ margin: 0, fontSize: 18, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}>
        {title}
      </h3>
    </div>
    {children}
  </Karta>
);

const StatusCard = ({ title, status, tone = "info", subtitle, source }) => {
  const tones = {
    ok:   { top: "#22c55e", icon: "#22c55e", text: "#111827", sub: "#6b7280", border: "#e6f6ec", Icon: CheckCircle2 },
    bad:  { top: "#ef4444", icon: "#ef4444", text: "#111827", sub: "#6b7280", border: "#fdecec", Icon: AlertCircle  },
    warn: { top: "#f59e0b", icon: "#f59e0b", text: "#111827", sub: "#6b7280", border: "#fef3c7", Icon: AlertCircle  },
    info: { top: "#3b82f6", icon: "#3b82f6", text: "#111827", sub: "#6b7280", border: "#dbeafe", Icon: Info        },
  };
  const c = tones[tone] || tones.info;
  const IconComp = c.Icon;

  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        background: "#fff",
        boxShadow: "0 6px 24px rgba(16,24,40,0.06)",
        overflow: "hidden",
        display: "grid",
      }}
    >
      <div style={{ height: 6, background: c.top }} />
      <div style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 12, padding: 14 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: 56,
            width: 56,
            borderRadius: "999px",
            border: `2px solid ${c.icon}`,
            color: c.icon,
          }}
        >
          <IconComp size={28} />
        </div>
        <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#6b7280", fontWeight: 700 }}>
            {title}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: c.text }}>
            {status ?? <em>—</em>}
          </div>
          {subtitle ? (
            <div style={{ fontSize: 12, color: c.sub }}>{subtitle}</div>
          ) : source ? (
            <div style={{ fontSize: 12, color: c.sub }}>
              Źródło danych: <strong>{source}</strong>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/** --- Helpers --- */
const normalize = (v) => (v == null ? "" : String(v).trim());
const yes = (v) => /^tak$/i.test(normalize(v));
const no  = (v) => /^nie$/i.test(normalize(v));
const capitalize = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

const pln = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 2 });
};
const formatNumeric = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("pl-PL");
};

const isDateValue = (val) => {
  const v = String(val ?? "").trim();
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(v) ||
    /^\d{4}-\d{2}-\d{2}t/i.test(v) ||
    /^\d{2}\.\d{2}\.\d{4}$/.test(v)
  );
};
const formatDate = (val) => {
  const v = String(val ?? "").trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    const d = new Date(v);
    if (!isNaN(d)) return d.toLocaleDateString("pl-PL");
  }
  return v || "—";
};

/** Polarity by key/title:
 *  - "negative": TAK = bad (red),  NIE = ok (green)
 *  - "positive": TAK = ok (green),  NIE = bad (red)
 *  - "neutral":  TAK/NIE = info (blue)
 */
function inferPolarity(key = "", label = "") {
  const t = `${key} ${label}`.toLowerCase();

  const negativeHints = [
    "zawieszen", "likwidac", "wykreśl", "wykreślen", "zamknię",
    "obniżenie kapita", "obnizenie kapita", "zmiana przedmiotu",
  ];
  const positiveHints = [
    "kapitał", "kapital", "wniesienie kapita", "status podmiotu",
    "aktywn", "czynny",
  ];

  if (negativeHints.some(h => t.includes(h))) return "negative";
  if (positiveHints.some(h => t.includes(h))) return "positive";
  return "neutral";
}

/** Map field value -> tone + display text with new color rules */
function mapValueToToneAndDisplay(key, label, raw) {
  // Empty or explicit "Brak danych" => neutral/blue
  if (raw == null || String(raw).trim() === "" || String(raw).trim().toLowerCase() === "brak danych") {
    return { tone: "info", display: "—" };
  }

  const val = String(raw).trim();
  const low = val.toLowerCase();

  // Dates & numbers -> neutral (blue), formatted
  if (isDateValue(val)) {
    return { tone: "info", display: formatDate(val) };
  }
  const num = Number(val.replace(/\s/g, "").replace(",", "."));
  if (Number.isFinite(num)) {
    // treat amounts/counts as neutral
    if (/(capital|kapita)/i.test(`${key} ${label}`)) return { tone: "info", display: pln(num) };
    return { tone: "info", display: formatNumeric(num) };
  }

  // Boolean-like values with polarity
  if (yes(val) || no(val)) {
    const polarity = inferPolarity(key, label);
    if (polarity === "negative") {
      return yes(val)
        ? { tone: "bad", display: "Tak" }
        : { tone: "ok",  display: "Nie" };
    }
    if (polarity === "positive") {
      return yes(val)
        ? { tone: "ok",  display: "Tak" }
        : { tone: "bad", display: "Nie" };
    }
    // neutral polarity -> blue for both
    return { tone: "info", display: capitalize(val) };
  }

  // Qualitative words -> keep warn/bad mapping; otherwise neutral
  if (low.includes("bardzo nisk")) return { tone: "ok",  display: capitalize(val) };
  if (low.includes("nisk"))        return { tone: "info", display: capitalize(val) };
  if (low.includes("średn") || low.includes("sredn") || low.includes("umiark"))
    return { tone: "warn", display: capitalize(val) };
  if (low.includes("wysok"))       return { tone: "bad", display: capitalize(val) };

  // Default neutral
  return { tone: "info", display: val || "—" };
}

/**
 * Props:
 *  - krs:   (object or null)
 *  - white: white_list object or null
 *  - risk:  full /td/risk/report result (we use .basic_data and .other_data)
 */
export default function StatusMonitoringSection({ krs, white, risk }) {
  // Two core monitoring statuses
  const vatStatus =
    white?.vat_status ||
    (yes(risk?.other_data?.pl_vat_payer?.value) ? "Czynny" : risk?.other_data?.pl_vat_payer?.value);

  const companyStatus = risk?.basic_data?.company_status?.value || "";

  // Force tones using the same boolean polarity logic for these two:
  const vatPolarity = "positive"; // "czynny" is positive
  const vatTone = yes(vatStatus)
    ? (vatPolarity === "positive" ? "ok" : "bad")
    : no(vatStatus)
      ? (vatPolarity === "positive" ? "bad" : "ok")
      : "info";

  const companyPolarity = inferPolarity("company_status", "Status działalności") || "neutral";
  const companyTone = (function () {
    const v = String(companyStatus || "").toLowerCase();
    if (!v) return "info";
    if (v.includes("aktywn")) return "ok";
    if (v.includes("zawies") || v.includes("likwidac") || v.includes("wykreśl") || v.includes("zamknię")) return "bad";
    // If it's a strict TAK/NIE value, fall back to polarity rule
    if (yes(v) || no(v)) {
      return yes(v)
        ? (companyPolarity === "negative" ? "bad" : companyPolarity === "positive" ? "ok" : "info")
        : (companyPolarity === "negative" ? "ok" : companyPolarity === "positive" ? "bad" : "info");
    }
    return "info";
  })();

  const monitoringItems = useMemo(
    () => [
      {
        key: "__vat_status",
        title: "Status VAT",
        status: vatStatus || "—",
        tone: vatTone,
        source: "Biała Lista / Raport ryzyka",
      },
      {
        key: "company_status",
        title: "Status działalności",
        status: companyStatus || "—",
        tone: companyTone,
        source: "Raport ryzyka (basic_data)",
      },
    ],
    [vatStatus, vatTone, companyStatus, companyTone]
  );

  // All additional cards from risk.basic_data (dedupe company_status)
  const blockedKeys = new Set(["company_status"]);
  const basicItems = Object.entries(risk?.basic_data ?? {})
    .filter(([key]) => !blockedKeys.has(key))
    .map(([key, obj]) => {
      const title = obj?.label_short || key;
      const subtitle = obj?.label_long || "";
      const value = obj?.value;
      const { tone, display } = mapValueToToneAndDisplay(key, title, value);
      return { key, title, status: display, tone, subtitle };
    });

  const allItems = [...monitoringItems, ...basicItems];

  return (
    <Sekcja title="Statusy monitoringu">
      <style>{`
        .statusGrid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }
        @media (max-width: 1280px) { .statusGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 1024px) { .statusGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px)  { .statusGrid { grid-template-columns: 1fr; } }
      `}</style>

      {allItems.length === 0 ? (
        <em>Brak danych.</em>
      ) : (
        <div className="statusGrid">
          {allItems.map((it) => (
            <StatusCard
              key={it.key}
              title={it.title}
              status={it.status}
              tone={it.tone}
              subtitle={it.subtitle}
              source={it.source}
            />
          ))}
        </div>
      )}
    </Sekcja>
  );
}
